export type EvidenceKind = 'fact' | 'mechanism' | 'inference' | 'opinion' | 'hypothesis';
export type Confidence = 'high' | 'medium' | 'low';
export type NodeKind = 'case' | 'mechanism' | 'condition' | 'outcome' | 'risk' | 'opportunity';
export type Stage = 0 | 1 | 2 | 3 | 4;
export type Lane = -1 | 0 | 1;
export type CategoryId = 'business' | 'technology' | 'platform' | 'finance' | 'power' | 'failure' | 'economics' | 'game' | 'history';
export type EdgeTone = 'cause' | 'enable' | 'risk';

export interface Source {
  id: string;
  title: string;
  publisher: string;
  year: string;
  url: string;
  note?: string;
}

export interface CausalNode {
  id: string;
  caseId: string;
  kind: NodeKind;
  stage: Stage;
  lane: Lane;
  title: string;
  summary: string;
  detail: string;
  boundary?: string;
  counter?: string;
  evidence: EvidenceKind;
  confidence: Confidence;
  sourceIds: string[];
}

export interface CausalEdge {
  id: string;
  caseId: string;
  from: string;
  to: string;
  label: string;
  explanation: string;
  tone: EdgeTone;
}

export interface Fact {
  text: string;
  sourceIds: string[];
}

export interface CaseStudy {
  id: string;
  order: number;
  title: string;
  english: string;
  category: CategoryId;
  era: string;
  region: string;
  accent: string;
  hook: string;
  question: string;
  summary: string;
  thesis: string;
  quote: string;
  /** 新收录且通过质量审核的案例，进入探索队列前部。 */
  isNew?: boolean;
  /** 默认展开的第一层节点；编辑选择，不是自动全部展示。 */
  entryNodeIds: string[];
  nodes: CausalNode[];
  edges: CausalEdge[];
  facts: Fact[];
  lensIds: string[];
  opportunityIds: string[];
  sourceIds: string[];
  focusNodeId: string;
}

export interface Lens {
  id: string;
  title: string;
  english: string;
  short: string;
  question: string;
  explain: string;
  mechanism: string;
  worksWhen: string;
  failsWhen: string;
  audit: string;
  caseIds: string[];
  sourceIds: string[];
}

export interface Opportunity {
  id: string;
  title: string;
  short: string;
  caseId: string;
  nodeIds: string[];
  lensIds: string[];
  type: string;
  capital: string;
  horizon: string;
  thesis: string;
  whoPays: string;
  whyNow: string;
  firstTest: string;
  economics: string;
  risks: string;
  confidence: Confidence;
  sourceIds: string[];
}

export interface OpportunityPlaybook {
  whyPay: string;
  acquisition: string;
  costStructure: string;
  competitors: string;
  profitSource: string;
  resources: string;
  legal: string;
  failurePoint: string;
}

export interface TrailStep {
  caseId: string;
  nodeId: string;
  prompt: string;
}

export interface Trail {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  duration: string;
  steps: TrailStep[];
}

export interface Category {
  id: CategoryId;
  title: string;
  short: string;
  color: string;
}

export const evidenceLabels: Record<EvidenceKind, { label: string; description: string }> = {
  fact: { label: '事实', description: '可由可靠资料核实的制度、事件或数据；仍需注意口径、时点与地区。' },
  mechanism: { label: '机制', description: '解释事实如何发生的因果模型；通常有理论依据，也可能有反例。' },
  inference: { label: '推断', description: '根据现有证据作出的判断，不等于已经被完全证实。' },
  opinion: { label: '观点', description: '有论据、可争论的判断；不应被当作事实。' },
  hypothesis: { label: '假设', description: '值得验证但证据不足的解释，需要主动寻找反证。' },
};

export const confidenceLabels: Record<Confidence, string> = {
  high: '证据较强',
  medium: '证据中等',
  low: '证据有限',
};

export const nodeKindLabels: Record<NodeKind, string> = {
  case: '现实入口',
  mechanism: '机制',
  condition: '前提',
  outcome: '结果',
  risk: '脆弱处',
  opportunity: '机会透镜',
};

export const categories: Category[] = [
  { id: 'business', title: '商业系统', short: '企业如何把一门生意做成系统', color: '#c65a3e' },
  { id: 'technology', title: '技术变迁', short: '新技术如何改变成本、接口与权力', color: '#367a6a' },
  { id: 'platform', title: '平台网络', short: '多方如何在同一个网络中互相塑造', color: '#516f9f' },
  { id: 'finance', title: '金融与危机', short: '期限、杠杆与信任如何积累和断裂', color: '#a7782f' },
  { id: 'power', title: '权力与制度', short: '法律、监管与议价权如何分配利益', color: '#7a5b76' },
  { id: 'failure', title: '失败与转型', short: '旧优势为何会在变化中变成负债', color: '#746f63' },
  { id: 'economics', title: '经济与金钱', short: '价格、信用、通胀与周期如何形成', color: '#a7782f' },
  { id: 'game', title: '博弈与策略', short: '多方在规则与信息下如何互相回应', color: '#7c5c93' },
  { id: 'history', title: '历史与制度', short: '基础设施、政策与制度变迁的长期结果', color: '#5f6e4e' },
];
